import React from "react";
import {
  Box,
  Card,
  CircularProgress,
  Divider,
  LinearProgress,
  Link,
  Typography,
} from "@mui/material";
import LogoutTwoToneIcon from "@mui/icons-material/LogoutTwoTone";
import { styled } from "@mui/material/styles";
import { useData } from "../providers/DataProvider";
import GradientButton from "./gradients/GradientButton";
import PurchasePatchPointsModal from "./modals/PurchasePatchPointsModal";
import AddToBagDisclaimerModal from "./modals/AddToBagDisclaimerModal";
import SaveDesignModal from "./modals/SaveDesignModal";
import MobileFooter from "./mobile/MobileFooter";
import ResetPasswordModal from "./modals/ResetPasswordModal";
import ColorSwitcher from "./ColorSwitcher";
import FavoritesModal from "./modals/mobile/FavoritesModal";
import HeartIcon from "./svg/HeartIcon";
import AuthModalSwitcher from "./AuthModalSwitcher";

const StyledPanel = styled(Card, {
  shouldForwardProp: (prop) => prop !== "kiosk",
})(({ theme, kiosk }) => ({
  position: "fixed",
  top: kiosk ? "160px" : "60px",
  left: "25px",
  width: "175px",
  zIndex: 2,
  boxShadow: "1px 14px 50px 0px #0000001C",
  "& > *": {
    padding: "10px 15px",
  },
  "& > .MuiDivider-root": {
    padding: "0px",
  },
  [theme.breakpoints.up("laptop")]: {
    width: "275px",
    "& > *": {
      padding: "15px 20px",
    },
  },
}));

const StyledProductTitle = styled(Typography)(({ theme }) => ({
  marginBottom: "5px",
  [theme.breakpoints.down("laptop")]: {
    fontSize: "16px",
    width: "145px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
}));

const StyledBoxFlexApart = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
}));

const StyledColorToggleLabel = styled(Typography)(({ theme, swatch }) => ({
  fontSize: "14px",
  fontWeight: "700",
}));

const StyledProgressCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.inverted,
  color: theme.palette.text.inverted,
  padding: "12px",
  marginBottom: "10px",
  textAlign: "center",
  [theme.breakpoints.down("laptop")]: {
    borderRadius: "unset",
    display: "flex",
    gap: "0.5rem",
    alignItems: "center",
    margin: "0px",
  },
}));

const StyledProgressBar = styled(LinearProgress)(({ theme, value }) => ({
  height: "10px",
  borderRadius: "5px",
  background: "#585858",
  "& > .MuiLinearProgress-bar": {
    background: `linear-gradient(90deg, 
      rgba(204,48,69,1) ${100 - value}%, 
      rgba(234,96,165,1) ${100 - value + value * 0.2}%, 
      rgba(245,225,124,1) ${100 - value + value * 0.4}%, 
      rgba(79,173,91,1) ${100 - value + value * 0.6}%, 
      rgba(50,152,196,1) ${100 - value + value * 0.8}%, 
      rgba(175,145,189,1) 100%
    )`,
    transition: "none",
  },
}));

const StyledBtnWrapper = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginTop: "10px",
  [theme.breakpoints.down("laptop")]: {
    flexDirection: "column",
  },
}));

function CustomizerPanel({
  cart,
  handleAddPatchPoints,
  handleCreateCheckout,
  handleSaveDesign,
  handleOpenCart,
  isMobile,
  favoritePatches,
  setFavoritePatches,
  setMobilePatch,
  customer,
}) {
  const data = useData();
  const [modal, setModal] = React.useState(null);

  const isDisabled = Boolean(cart.loading || cart.pointsUsed > cart.pointsAvailable);

  const handleAddToBag = () => {
    data.audios.ADD_TO_BAG.play();

    setModal({
      type: "ATB_DISCLAIMER",
    });
  };

  const handleMorePoints = () => {
    setModal({ type: "PURCHASE_POINTS" });
  };

  const handleFavoritePatches = () => {
    data.audios.SELECTING_CATEGORY.play();
    setModal({ type: "FAVORITE_PATCHES" });
  };

  const onSaveDesign = () => {
    if (!data.token) {
      setModal({
        type: "SIGN_IN",
        initCheckout: true,
        prevType: "SAVE_DESIGN",
      });
    } else {
      setModal({ type: "SAVE_DESIGN" });
    }
  };

  const handleCloseModal = () => {
    setModal(null);
  };

  const getProgressBarValue = (value) => {
    return value <= 100 ? value : 100;
  };

  const renderModals = () => {
    return (
      <>
        <AuthModalSwitcher
          onClose={handleCloseModal}
          setModal={setModal}
          modal={modal}
          isMobile={isMobile}
        />
        {modal && modal.type === "PURCHASE_POINTS" && (
          <PurchasePatchPointsModal
            onClose={handleCloseModal}
            handleAddPatchPoints={handleAddPatchPoints}
            isMobile={isMobile}
          />
        )}
        {modal && modal.type === "ATB_DISCLAIMER" && (
          <AddToBagDisclaimerModal
            customer={customer}
            onClose={handleCloseModal}
            onModalAction={setModal}
            handleCreateCheckout={handleCreateCheckout}
            isMobile={isMobile}
          />
        )}
        {modal && modal.type === "SAVE_DESIGN" && (
          <SaveDesignModal
            onClose={handleCloseModal}
            handleSaveDesign={handleSaveDesign}
            isMobile={isMobile}
          />
        )}
        {modal && modal.type === "RESET_PASSWORD" && (
          <ResetPasswordModal onClose={handleCloseModal} isMobile={isMobile} />
        )}
        {modal && modal.type === "FAVORITE_PATCHES" && (
          <FavoritesModal
            onClose={handleCloseModal}
            favoritePatches={favoritePatches}
            handleFavoritePatch={setFavoritePatches}
            handleCardClick={setMobilePatch}
            isMobile={isMobile}
          />
        )}
      </>
    );
  };

  // Render.

  if (isMobile) {
    const iconStyle = {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "5px",
      minWidth: "85px",
    };

    return (
      <MobileFooter>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-around",
            marginBottom: "1rem",
          }}
        >
          <Box sx={iconStyle} onClick={handleFavoritePatches}>
            <HeartIcon />
            <Link>My Favorites</Link>
          </Box>
          <GradientButton
            disabled={isDisabled}
            addStyle={{ minHeight: "45px", fontSize: "15px" }}
            onClick={handleAddToBag}
          >
            Add to Bag
          </GradientButton>
          <Box sx={iconStyle} onClick={onSaveDesign}>
            <LogoutTwoToneIcon color="disabled" />
            <Link>Save Design</Link>
          </Box>
        </Box>
        <StyledProgressCard>
          <Typography sx={{ marginBottom: "0px" }} variant="h4">
            Patch Points
          </Typography>
          <Typography>{cart.pointsUsed} used</Typography>
          <Box sx={{ flexGrow: "1" }}>
            <StyledProgressBar
              variant="determinate"
              value={getProgressBarValue((cart.pointsUsed / cart.pointsAvailable) * 100)}
            />
          </Box>
          <Box>
            <Typography>{cart.pointsAvailable}</Typography>
          </Box>
        </StyledProgressCard>
        {
          /* modals */
          renderModals()
        }
      </MobileFooter>
    );
  }

  return (
    <StyledPanel kiosk={data.isKiosk}>
      <Box>
        <StyledProductTitle variant="h1">{data.product.title}</StyledProductTitle>
        <StyledColorToggleLabel>Color:</StyledColorToggleLabel>
        <ColorSwitcher data={data} />
      </Box>
      <Divider />
      <Box>
        <StyledProgressCard elevation={0}>
          <Typography variant="h4">Patch Points</Typography>
          <StyledBoxFlexApart>
            <Typography>{cart.pointsUsed} used</Typography>
            <Typography>{cart.pointsAvailable}</Typography>
          </StyledBoxFlexApart>
          <StyledProgressBar
            variant="determinate"
            value={getProgressBarValue((cart.pointsUsed / cart.pointsAvailable) * 100)}
          />
          <GradientButton
            sx={{
              marginTop: "15px",
              "& > .MuiBox-root": {
                paddingLeft: "24px",
                paddingRight: "24px",
              },
            }}
            onClick={handleMorePoints}
          >
            Add Patch Points
          </GradientButton>
        </StyledProgressCard>
        <StyledBoxFlexApart>
          <Typography>
            Your Order <Link onClick={handleOpenCart}>(cart)</Link>
          </Typography>
          <Typography sx={{ fontWeight: "700" }}>${cart.total.toFixed(2)}</Typography>
        </StyledBoxFlexApart>
        <StyledBtnWrapper>
          <GradientButton disabled={isDisabled} onClick={handleAddToBag} fullWidth>
            Add to Bag
            {cart.loading && (
              <CircularProgress
                color="inherit"
                size="1rem"
                thickness={6}
                sx={{
                  position: "absolute",
                  right: "16px",
                }}
              />
            )}
          </GradientButton>
          <GradientButton onClick={onSaveDesign} fullWidth>
            Save Design
          </GradientButton>
        </StyledBtnWrapper>
        {cart.pointsUsed > cart.pointsAvailable && (
          <Typography sx={{ color: "red", marginTop: "10px" }}>
            You need more patch points to purchase this design!
          </Typography>
        )}
      </Box>
      <Divider />
      {!data.customer && (
        <Box>
          <Typography sx={{ textAlign: "center" }}>
            <Link onClick={() => setModal({ type: "SIGN_IN" })}>Sign In</Link> or{" "}
            <Link onClick={() => setModal({ type: "SIGN_UP" })}>Sign Up</Link> to save your
            progress!
          </Typography>
        </Box>
      )}

      {
        /* modals */
        renderModals()
      }
    </StyledPanel>
  );
}

export default React.memo(CustomizerPanel);
